/**
 * UserNotesList Component
 *
 * Displays user's notes list with the modern design, including optional
 * audit view for payer template processing feature.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';

import Pagination from './Pagination';
import { useFeatureFlag } from '@features/user';
import { useGetJotCoachEncounterSessionsQuery, QualityScoreDisplay } from '@features/jotcoach';
import type { EncounterSessionResponse } from '@store/generated/jotcoach';
import { NotesListPage, NotesListContent } from '@features/notes';

export const UserNotesList: React.FC = () => {
  const navigate = useNavigate();
  const isPayerTemplateProcessingEnabled = useFeatureFlag('payer_template_note_processing');

  const [viewMode, setViewMode] = useState<'list' | 'audit'>('list');
  const [encounterPage, setEncounterPage] = useState(1);
  const [encounterPerPage, setEncounterPerPage] = useState(20);

  // Fetch encounter sessions for audit view
  const {
    data: encounterSessionsData,
    isLoading: encounterSessionsLoading,
    error: encounterSessionsError,
  } = useGetJotCoachEncounterSessionsQuery(
    {
      page: encounterPage,
      perPage: encounterPerPage,
    },
    {
      skip: viewMode !== 'audit' || !isPayerTemplateProcessingEnabled,
    },
  );

  // If no audit view feature, render the modern notes list directly
  if (!isPayerTemplateProcessingEnabled) {
    return <NotesListPage title="Notes" />;
  }

  // With audit view feature enabled, show tabs
  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Tabs
        value={viewMode}
        onChange={(_, newValue) => setViewMode(newValue)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
        }}
      >
        <Tab label="List View" value="list" />
        <Tab label="Audit View" value="audit" />
      </Tabs>

      {viewMode === 'list' && <NotesListContent title="Notes" />}

      {viewMode === 'audit' && (
        <AuditView
          encounterSessionsData={encounterSessionsData}
          encounterSessionsLoading={encounterSessionsLoading}
          encounterSessionsError={encounterSessionsError}
          encounterPage={encounterPage}
          setEncounterPage={setEncounterPage}
          encounterPerPage={encounterPerPage}
          setEncounterPerPage={setEncounterPerPage}
          navigate={navigate}
        />
      )}
    </Box>
  );
};

/**
 * Audit View Component - displays JotCoach encounter sessions
 */
interface AuditViewProps {
  encounterSessionsData: any;
  encounterSessionsLoading: boolean;
  encounterSessionsError: any;
  encounterPage: number;
  setEncounterPage: (page: number) => void;
  encounterPerPage: number;
  setEncounterPerPage: (perPage: number) => void;
  navigate: (path: string, options?: any) => void;
}

const AuditView: React.FC<AuditViewProps> = ({
  encounterSessionsData,
  encounterSessionsLoading,
  encounterSessionsError,
  encounterPage,
  setEncounterPage,
  encounterPerPage,
  setEncounterPerPage,
  navigate,
}) => {
  if (encounterSessionsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '25vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (encounterSessionsError) {
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: 'error.light',
          color: 'error.contrastText',
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography>Error loading encounter sessions. Please try again.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          JotCoach Encounter Sessions
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Select
            value={encounterPerPage}
            onChange={e => {
              setEncounterPerPage(Number(e.target.value));
              setEncounterPage(1);
            }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value={20}>20 per page</MenuItem>
            <MenuItem value={50}>50 per page</MenuItem>
            <MenuItem value={100}>100 per page</MenuItem>
          </Select>
        </Box>
      </Box>

      {encounterSessionsData?.items && encounterSessionsData.items.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {encounterSessionsData.items.map(
            (
              session: EncounterSessionResponse & {
                note_id?: string | null;
                flags_summary?: string | null;
                overall_quality_score?: number | null;
                document_id?: string;
                document_type?: string;
                patient_id?: string | null;
                patient_name?: string | null;
                provider_name?: string | null;
                selected_cpt_code?: string | null;
              },
            ) => {
              let rulesAnalysis: any = null;
              let noteId: string | null = null;
              let jotdocId: string | null = null;

              if ((session as any).note_id) {
                noteId = (session as any).note_id;
                if (
                  (session as any).flags_summary ||
                  (session as any).overall_quality_score !== undefined
                ) {
                  rulesAnalysis = {
                    flags_summary: (session as any).flags_summary,
                    overall_quality_score: (session as any).overall_quality_score,
                    billing_support:
                      (session as any).proposed_primary_code || (session as any).selected_cpt_code
                        ? {
                            proposed_primary_code:
                              (session as any).proposed_primary_code ||
                              (session as any).selected_cpt_code,
                          }
                        : undefined,
                  };
                }
              } else if (session.sessions) {
                for (const [docKey, docData] of Object.entries(session.sessions)) {
                  if (docData && typeof docData === 'object' && 'rules_analysis' in docData) {
                    rulesAnalysis = docData.rules_analysis;
                    if (docKey.startsWith('note:')) {
                      noteId = docKey.replace('note:', '');
                    } else if (docKey.startsWith('jotdoc:')) {
                      jotdocId = docKey.replace('jotdoc:', '');
                      noteId = (docData as any).note_id || null;
                    }
                    break;
                  }
                }
              }

              const sessionDataFlat = session as any;
              if (sessionDataFlat.document_type === 'jotdoc' && sessionDataFlat.document_id) {
                jotdocId = sessionDataFlat.document_id;
              }

              const formatDateStr = (dateString: string) => new Date(dateString).toLocaleString();

              const hasNavigationTarget = jotdocId || noteId;
              const handleCardClick = () => {
                if (jotdocId) {
                  navigate(`/jotdoc/${jotdocId}`, {
                    state: { noteId, encounterId: session.encounter_id },
                  });
                } else if (noteId) {
                  navigate(`/note/${noteId}`);
                }
              };

              return (
                <Card
                  key={session.jotcoach_encounter_session_id}
                  onClick={handleCardClick}
                  sx={{
                    mb: 2,
                    cursor: hasNavigationTarget ? 'pointer' : 'default',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': hasNavigationTarget
                      ? {
                          boxShadow: 4,
                          borderColor: 'primary.main',
                          transition: 'all 0.2s ease',
                        }
                      : {},
                  }}
                >
                  <CardContent>
                    <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Patient Name
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {sessionDataFlat.patient_name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Provider Name
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {sessionDataFlat.provider_name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          CPT Code
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {sessionDataFlat.selected_cpt_code || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Created At
                        </Typography>
                        <Typography variant="body2">{formatDateStr(session.created_at)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 1 }}
                        >
                          Quality Score
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                          <QualityScoreDisplay
                            score={rulesAnalysis?.overall_quality_score ?? 0}
                            showLabel={false}
                            size={60}
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    {rulesAnalysis?.flags_summary && (
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'primary.light',
                          borderLeftWidth: 4,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                          {rulesAnalysis.flags_summary}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            },
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '25vh',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No encounter sessions found
          </Typography>
        </Box>
      )}

      {encounterSessionsData?.total && encounterSessionsData.total > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 1,
            marginTop: 2,
          }}
        >
          <Pagination
            currentPage={encounterPage}
            itemsPerPage={encounterPerPage}
            totalItems={encounterSessionsData.total || 0}
            onPageChange={setEncounterPage}
          />
        </Box>
      )}
    </>
  );
};
